import { type CSSProperties, forwardRef } from 'react';

import { Progress as BaseProgress } from '@base-ui/react/progress';
import { cn } from 'components/utils';

import { progressFillRecipe, progressRootRecipe, progressTrackRecipe } from './Progress.recipe';

import type { ProgressProps } from './types';

/**
 * Design System 2.0 progress bar: how far a task has got, when the system knows.
 *
 * A bar only. The label and the number beside it are composed by the caller, so each surface says
 * what is progressing ("Uploading", "Topic 2 of 5") in its own words. When the total is unknown, use
 * a spinner instead — this component has no indeterminate state, because a bar that moves without
 * meaning anything is a fake.
 *
 * Built on Base UI's `Progress`, which supplies `role="progressbar"` and the value attributes.
 */
export const Progress = forwardRef<HTMLDivElement, ProgressProps>(
  ({ value, size = 'md', tone = 'accent', className, style, ...rest }, ref) => {
    const clamped = Math.min(100, Math.max(0, value));

    return (
      <BaseProgress.Root
        ref={ref}
        value={clamped}
        locale="en-GB"
        className={cn(progressRootRecipe(), className)}
        style={{ ...style, '--ds-progress-value': `${clamped}%` } as CSSProperties}
        {...rest}
      >
        <BaseProgress.Track className={progressTrackRecipe({ size })}>
          <span className={progressFillRecipe({ tone })} data-part="fill" />
        </BaseProgress.Track>
      </BaseProgress.Root>
    );
  }
);
